"use client";

import { FC, Fragment, useCallback } from "react";
import classNames from "classnames";
import { IBetResponse, ITicketResponse } from "@finbackoffice/clientbff-client";
import { useCollapseMargin } from "@finbackoffice/site-core";
import { AssetsDirectory, TicketStatus, TicketType, TranslationScopes } from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import { useDate, DEFAULT_DATE_FORMAT } from "hooks";
import { CurrencyFormatter } from "components/base/currency-formater/CurrencyFormater";
import { Svg } from "components/base/svg/Svg";
import SportIcon from "components/base/sport-icon/SportIcon";
import styles from "./sport-bet-row.module.sass";

interface IProps {
    outcome: IBetResponse;
    bet: ITicketResponse;
}

const SportSingleBetView: FC<IProps> = ({ outcome, bet }) => {
    const { formatDate } = useDate();
    const { opened, animating, headerRef, containerRef } = useCollapseMargin(false);
    const outcomePlaceState = outcome.place_state;
    const outcomeCloseState = outcome.close_state;

    const renderPeriodScore = useCallback(
        (period: any, index: number, allPeriods: any[]) => (
            <Fragment key={index}>
                <span>{period.home_score}</span>:<span>{period.away_score}</span>
                {index !== allPeriods.length - 1 && ","}
            </Fragment>
        ),
        [],
    );

    return (
        <div
            className={classNames(styles.bet, styles[`base-icon-${outcome.status.toLowerCase()}`])}>
            {bet.type === TicketType.Single && (
                <div>
                    <span className={styles.label}>
                        <Translate
                            tid="account_bets_id"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    <strong>{bet.short_id}</strong>
                    <span className={styles.label}>
                        {formatDate(
                            new Date(bet.created_at),
                            `${DEFAULT_DATE_FORMAT} HH:mm`,
                            false,
                        )}
                    </span>
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/status-${bet.status.toLowerCase()}.svg`}
                        className="svg-icon"
                    />
                </div>
            )}
            <div className={styles.game}>
                <SportIcon sportId={outcome.sport_id} />
                {bet.type === TicketType.Single && `${bet.type} - `}
                {outcome.event_status ? (
                    <Translate
                        tid={`account_bets_sportService_${outcome.event_status.toLowerCase()}`}
                        namespace={TranslationScopes.UserAccount}
                    />
                ) : (
                    "- -"
                )}
                {` - ${outcome.sport_name}`}
                {bet.type !== TicketType.Single && (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/status-${outcome.status.toLowerCase()}.svg`}
                        className="svg-icon bet-status-svg"
                    />
                )}
            </div>
            <div className={styles.info}>
                <p>
                    <span>{outcome.category_name}</span> / <span>{outcome.tournament_name}</span>
                </p>
                <p>
                    <span>{outcome.home_team}</span> - <span>{outcome.away_team}</span>
                </p>
                <p>
                    <span className={styles.label}>
                        <Translate
                            tid="account_bets_eventDate"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    <span>
                        {outcome.scheduled_at &&
                            formatDate(
                                new Date(outcome.scheduled_at),
                                `${DEFAULT_DATE_FORMAT} HH:mm`,
                                false,
                            )}
                    </span>
                </p>
                <p>
                    <span className={styles.label}>
                        <Translate
                            tid="account_bets_bet"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    {outcome.market_name}
                </p>
                <p>
                    <span className={styles.label}>
                        <Translate
                            tid="account_bets_pick"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    {outcome.outcome_name}
                    <strong className={styles.odds}>
                        <span>Odds:</span>
                        {outcome.odds}
                    </strong>
                </p>
            </div>
            {bet.type === TicketType.Single && (
                <div>
                    <p>
                        <span className={styles.label}>
                            <Translate
                                tid="account_bets_stake"
                                namespace={TranslationScopes.UserAccount}
                            />
                            :
                        </span>
                        <strong>
                            <CurrencyFormatter
                                currency={bet.currency}
                                amount={bet.amount}
                                withCode={false}
                                withSymbol
                            />
                        </strong>

                        <span className={styles.label}>
                            <Translate
                                tid="account_bets_result"
                                namespace={TranslationScopes.UserAccount}
                            />
                            :
                        </span>
                        <strong>
                            {bet.status === TicketStatus.Accepted ? (
                                "- -"
                            ) : (
                                <CurrencyFormatter
                                    currency={bet.currency}
                                    amount={bet.payout}
                                    withCode={false}
                                    withSymbol
                                />
                            )}
                        </strong>
                    </p>
                    <p>
                        <span className={styles.label}>
                            <Translate
                                tid="account_bets_posWin"
                                namespace={TranslationScopes.UserAccount}
                            />
                            :
                        </span>
                        <strong>
                            <CurrencyFormatter
                                currency={bet.currency}
                                amount={bet.possible_win}
                                withCode={false}
                                withSymbol
                            />
                        </strong>
                        {/* <span className={styles.type}>Free Bet</span> */}
                    </p>
                </div>
            )}
            {/* BONUS  <div className={styles.bonus}>
        Bonus:{Chars.nbsp}
        <strong>500</strong>
        <span className={styles.label}>Percentage:</span>
        <strong>5%</strong>
    </div> */}
            {(opened || animating) && (
                <div className={styles.details} ref={containerRef}>
                    {outcomePlaceState && (
                        <div>
                            <strong>
                                <Translate
                                    tid="account_bets_placedAt"
                                    namespace={TranslationScopes.UserAccount}
                                />
                                :
                                <span>
                                    {formatDate(
                                        new Date(bet.created_at),
                                        `${DEFAULT_DATE_FORMAT} HH:mm:ss`,
                                        false,
                                    )}
                                </span>
                            </strong>
                            <p>
                                <span className={styles.label}>
                                    <Translate
                                        tid="account_bets_mainScore"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                    :
                                </span>
                                {`${outcomePlaceState.home_score}:${outcomePlaceState.away_score}`}
                            </p>
                            <p>
                                <span className={styles.label}>
                                    <Translate
                                        tid="account_bets_scores"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                    :
                                </span>
                                {outcomePlaceState.period_scores ? (
                                    <>
                                        (
                                        {outcomePlaceState.period_scores.map(
                                            (period: any, index: number) =>
                                                renderPeriodScore(
                                                    period,
                                                    index,
                                                    outcomePlaceState.period_scores,
                                                ),
                                        )}
                                        )
                                    </>
                                ) : (
                                    "N/A"
                                )}
                            </p>
                            <p>
                                <span className={styles.label}>
                                    <Translate
                                        tid="account_bets_status"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                    :
                                </span>
                                {outcome.match_status}
                            </p>
                        </div>
                    )}
                    {outcomeCloseState && (
                        <div>
                            <strong>
                                <Translate
                                    tid="account_bets_settledAt"
                                    namespace={TranslationScopes.UserAccount}
                                />
                                {bet.closed_at && (
                                    <>
                                        :
                                        <span>
                                            {formatDate(
                                                new Date(bet.closed_at),
                                                `${DEFAULT_DATE_FORMAT} HH:mm:ss`,
                                                false,
                                            )}
                                        </span>
                                    </>
                                )}
                            </strong>
                            <p>
                                <span className={styles.label}>
                                    <Translate
                                        tid="account_bets_mainScore"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                    :
                                </span>
                                <span>{outcomeCloseState.home_score}</span>:
                                <span>{outcomeCloseState.away_score}</span>
                            </p>
                            <p>
                                <span className={styles.label}>
                                    <Translate
                                        tid="account_bets_scores"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                    :
                                </span>
                                {outcomeCloseState.period_scores ? (
                                    <>
                                        (
                                        {outcomeCloseState.period_scores.map(
                                            (period: any, index: number) =>
                                                renderPeriodScore(
                                                    period,
                                                    index,
                                                    outcomeCloseState.period_scores,
                                                ),
                                        )}
                                        )
                                    </>
                                ) : (
                                    "N/A"
                                )}
                            </p>
                            <p>
                                <span className={styles.label}>
                                    <Translate
                                        tid="account_bets_status"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                    :
                                </span>
                                {outcome.close_state.status}
                            </p>
                        </div>
                    )}
                </div>
            )}
            <span
                className={classNames(styles.detailsBtn, (opened || animating) && styles.opened)}
                ref={headerRef}>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/arrow-white.svg`}
                    className="svg-icon"
                />
            </span>
        </div>
    );
};

export default SportSingleBetView;
